import { escapeHtml } from "@/lib/tool-kit/citation-parts";

/**
 * Formatted output for the journal-statement tools.
 *
 * A statement is a list of paragraphs and a paragraph is a list of runs, so the same
 * value can be drawn on the page (bold labels, superscript affiliation marks), copied
 * as HTML that Word and Google Docs keep formatted, and copied as plain text for a
 * submission form that strips everything. Building one structure and deriving all three
 * is what keeps them from disagreeing.
 */

export interface RichRun {
  text: string;
  bold?: boolean;
  sup?: boolean;
}

export type RichParagraph = RichRun[];

/** Unicode superscripts for the plain-text twin of an affiliation mark. */
const SUPERSCRIPTS: Record<string, string> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
  a: "ᵃ",
  b: "ᵇ",
  c: "ᶜ",
  d: "ᵈ",
  e: "ᵉ",
  f: "ᶠ",
  g: "ᵍ",
  h: "ʰ",
  i: "ⁱ",
  j: "ʲ",
  k: "ᵏ",
  l: "ˡ",
  m: "ᵐ",
  n: "ⁿ",
  o: "ᵒ",
  p: "ᵖ",
  r: "ʳ",
  s: "ˢ",
  t: "ᵗ",
  u: "ᵘ",
  v: "ᵛ",
  w: "ʷ",
  x: "ˣ",
  y: "ʸ",
  z: "ᶻ",
};

/**
 * Raise digits and lowercase letters to their Unicode superscript forms. Symbols (†, *,
 * the comma) stay as they are, and so does "q", which has no widely supported
 * superscript form.
 */
export function toUnicodeSuperscript(text: string): string {
  let out = "";
  for (const char of text) {
    out += SUPERSCRIPTS[char] ?? char;
  }
  return out;
}

export interface PlainOptions {
  /** Write superscript runs with Unicode superscripts instead of as ordinary text. */
  unicodeSuperscript?: boolean;
  /** Between paragraphs. A blank line by default. */
  paragraphSeparator?: string;
}

function runToPlain(run: RichRun, unicode: boolean): string {
  return run.sup && unicode ? toUnicodeSuperscript(run.text) : run.text;
}

export function paragraphToPlain(
  paragraph: RichParagraph,
  options: PlainOptions = {}
): string {
  const unicode = options.unicodeSuperscript ?? false;
  return paragraph.map((run) => runToPlain(run, unicode)).join("");
}

export function paragraphsToPlain(
  paragraphs: RichParagraph[],
  options: PlainOptions = {}
): string {
  return paragraphs
    .map((paragraph) => paragraphToPlain(paragraph, options))
    .join(options.paragraphSeparator ?? "\n\n");
}

function runToHtml(run: RichRun): string {
  let html = escapeHtml(run.text);
  if (run.sup) {
    html = `<sup>${html}</sup>`;
  }
  if (run.bold) {
    html = `<strong>${html}</strong>`;
  }
  return html;
}

export function paragraphToHtml(paragraph: RichParagraph): string {
  return paragraph.map(runToHtml).join("");
}

/** One `<p>` per paragraph — the form `CopyButton`'s `html` prop wants. */
export function paragraphsToHtml(paragraphs: RichParagraph[]): string {
  return paragraphs
    .map((paragraph) => `<p>${paragraphToHtml(paragraph)}</p>`)
    .join("");
}

/** A paragraph of one plain run. */
export function plainParagraph(text: string): RichParagraph {
  return [{ text }];
}

/** "Label: text" with the label in bold — how journals set statement headings. */
export function labelledParagraph(
  label: string,
  text: string,
  separator = " "
): RichParagraph {
  return [{ text: label, bold: true }, { text: `${separator}${text}` }];
}
